import React, { useState, useEffect } from "react";
import { Popover, Button, TextField, Box } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Column, ColumnFiltersState } from "@tanstack/react-table";
import { CustomColumnDef } from "./TableContainer";
import { autocompleteDropdownStyles } from "Constants/Style";
import { Axios } from "api/axios";
import { fetchPackages } from "slices/package/thunk";
import { toast } from "react-toastify";
import {
  formatLabel,
  formatLabel2,
  formatLabel3,
} from "helpers/functions/formatLabel";

interface FilterPopoverProps<TData> {
  column: Column<TData, unknown>;
  columnDef: CustomColumnDef<TData>;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setColValue?: any;
  iconColor?: string;
  scrollableFilterPopover?: boolean;
}

const FilterPopover = ({
  column,
  columnDef,
  columnFilters,
  setColumnFilters,
  setColValue,
  iconColor,
  scrollableFilterPopover = false,
}: FilterPopoverProps<any>) => {
  const filterType = columnDef.filterType;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempValue, setTempValue] = useState(column?.getFilterValue() || "");
  const [name, setName] = useState<string>("name");

  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<Element>) => {
    setAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    // setAnchorEl(null);

    // column.setFilterValue(undefined); // or `null` also works

    // if (setColValue) {
    //   setColValue((prev: any) =>
    //     (prev || []).filter(
    //       (f: { filterField: string }) => f.filterField !== column.id
    //     )
    //   );
    // }

    // setTempValue("");
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (!setColValue) return;

    if (tempValue && tempValue !== "" && tempValue !== "null") {
      column.setFilterValue(tempValue);
      setColValue((prev: any) => {
        const updated = (prev || []).filter(
          (f: { filterField: string }) => f.filterField !== column.id
        );
        return [...updated, { filterField: column.id, filterValue: tempValue }];
      });
    } else {
      column.setFilterValue(undefined);
      setColValue((prev: any) =>
        (prev || []).filter(
          (f: { filterField: string }) => f.filterField !== column.id
        )
      );
    }

    setAnchorEl(null);
  };

  const handleClear = () => {
    // Clear the filter from the table column
    column.setFilterValue(undefined);

    // Remove it from your local filter state
    if (setColValue) {
      setColValue((prev: any) =>
        (prev || []).filter(
          (f: { filterField: string }) => f.filterField !== column.id
        )
      );
    }

    // Clear the input value
    setTempValue("");
  };

  useEffect(() => {
    const appliedFilter = column.getFilterValue();
  }, []);

  useEffect(() => {
    if (open && columnDef.filterFetchUrl) {
      const fetchOptions = async () => {
        setLoading(true);
        try {
          const response = await Axios.get(columnDef.filterFetchUrl || "");

          const data: any = response.data;

          console.log("data in dropdown: ", data);

          if (data.data instanceof Array) {
            setOptions(data.data);
          } else {
            setOptions(data[column.id]);
          }
        } catch (error) {
          toast.error("Failed to fetch filter options");
          console.error("Failed to fetch filter options:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchOptions();
      if (open) {
        const appliedFilter = column.getFilterValue();
        setTempValue(appliedFilter || "");
      }
    }
  }, [open, columnDef.filterFetchUrl]);

  return (
    <>
      <FilterAltIcon
        fontSize="small"
        onClick={handleOpen}
        style={{ cursor: "pointer", marginLeft: 6, color: iconColor }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} width={200} sx={{ fontFamily: 'Poppins, sans-serif' }}>
          {filterType === "dropdown" ? (
            <Autocomplete
              sx={{ width: "100%", textTransform: "capitalize" }}
              size="small"
              id="filter-autocomplete"
              options={options || []}
              loading={isLoading}
              value={
                options?.find((opt) => {
                  if (
                    typeof opt === "string" &&
                    typeof tempValue === "string"
                  ) {
                    return opt === tempValue;
                  }
                  return String(opt) === String(tempValue);
                }) ?? null
              }
              // getOptionLabel={(option: any) => option?.toString()}
              getOptionLabel={(option: any) => formatLabel3(option?.toString())}
              isOptionEqualToValue={(option: any, value: any) => {
                if (typeof option === "string" && typeof value === "string") {
                  return option === value;
                }
                return String(option) === String(value);
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option: any, state) => {
                const { key, ...rest } = props;
                return (
                  <Box
                    key={state.index}
                    component="li"
                    {...rest}
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      // ...(scrollableFilterPopover
                      //   ? { fontWeight: '600' }
                      //   : {}),
                    }}
                  >
                    {/* {option?.toString()} */}
                    {formatLabel3(option?.toString())}
                  </Box>
                );
              }}
              onChange={(_, newValue) => {
                setTempValue(String(newValue) ?? "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '13px',
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '13px',
                    },
                  }}
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              label="Search"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              size="small"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& input': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                },
              }}
            />
          )}

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              className="btn btn-secondary"
              size="small"
              variant="outlined"
              onClick={handleClear}
              sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default FilterPopover;
